// src/utils/discoveryPools.js
//
// Pure, side-effect-free discovery logic. Extracted out of Discover.jsx
// so the compact rails (on the Discover/Gallery page) and the new full
// "See all" pages compute pools from the exact same rules — no drift
// between what a rail teases and what the full page actually shows.
//
// Nothing in this file talks to Firestore. It only transforms arrays
// of creators/drops that have already been fetched (see
// hooks/useDiscoveryData.js for the fetching side).

import { normalizeCreator } from "./homeSeedPreview";
import { isOpenFeedDrop } from "../services/dropsFeedService";
import { getNicheMeta, normalizeNicheKey } from "../components/niche";

// ── constants ──────────────────────────────────────────────────
export const NEW_CREATOR_WINDOW_DAYS = 14;
export const NEW_CREATOR_MAX_DROPS = 2;
export const FRESH_MOMENTS_LIMIT = 10;
export const RISING_LIMIT = 6;
export const NEAR_YOU_LIMIT = 6;

// ── small helpers ──────────────────────────────────────────────
export function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

export function pctViewed(drop) {
  const max = drop.maxViews || 100;
  return Math.min(100, Math.round(((drop.viewCount || 0) / max) * 100));
}

export function matchesSearch(text, q) {
  if (!q) return true;
  return text.toLowerCase().includes(q.toLowerCase());
}

export function thumbnailUrl(url) {
  const fallback =
    "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?q=85&fit=crop&w=900";
  if (!url) return fallback;
  if (url.includes("images.unsplash.com")) {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set("q", "85");
      parsed.searchParams.set("fit", "crop");
      parsed.searchParams.set("w", "900");
      return parsed.toString();
    } catch {
      return url;
    }
  }
  return url;
}

export function getDropAccess(drop, purchasedDropIds, subscribedCreatorIds) {
  if (drop.status === "closed") return { type: "closed" };
  if (drop.accessType === "free" || drop.price === 0) return { type: "free" };
  if (drop.accessType === "paid") {
    return purchasedDropIds.has(drop.id)
      ? { type: "paid-unlocked" }
      : { type: "paid-locked" };
  }
  if (drop.accessType === "subscription") {
    return subscribedCreatorIds.has(drop.creatorId)
      ? { type: "sub-unlocked" }
      : { type: "sub-locked" };
  }
  return { type: "free" };
}

// Deterministic-per-session shuffle. Same seed => same order for the
// whole session (rails/pages don't jump around while browsing), fresh
// seed next visit. NOT based on any user behavior — a fair random draw
// from the eligible pool, which is the whole point.
export function seededShuffle(arr, seed) {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// A creator qualifies for Rising if recently joined OR has posted very
// few drops so far — a guaranteed-exposure rule, not a ranking. Can't
// be earned by performance, can't be bought.
export function isNewCreator(creator, totalDrops) {
  const joinedMillis = toMillis(creator.createdAt || creator.joinedAt);
  const withinWindow =
    joinedMillis > 0 &&
    Date.now() - joinedMillis < NEW_CREATOR_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const lowDropCount = (totalDrops ?? 0) <= NEW_CREATOR_MAX_DROPS;
  return withinWindow || lowDropCount;
}

// ── pool builders ──────────────────────────────────────────────
// Each of these takes the raw fetched data plus an `options` object
// ({ niche, search, limit }) and returns a ready-to-render array.
// `limit: null` means "return everything" — used by the full pages;
// the compact rails on Discover.jsx pass the small LIMIT constants.

export function computeFreshMoments(drops, creatorById, { niche = "all", search = "", excludeIds = new Set(), limit = FRESH_MOMENTS_LIMIT } = {}) {
  const trimmed = search.trim();
  const pool = drops
    .filter((d) => isOpenFeedDrop(d) && !excludeIds.has(d.id))
    .filter((d) => {
      if (niche !== "all" && normalizeNicheKey(d.niche) !== niche) return false;
      if (trimmed) {
        const creator = creatorById.get(d.creatorId);
        const haystack = [d.title, d.description, creator?.displayName, creator?.handle, getNicheMeta(d.niche).label]
          .filter(Boolean)
          .join(" ");
        if (!matchesSearch(haystack, trimmed)) return false;
      }
      return true;
    })
    .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  return limit == null ? pool : pool.slice(0, limit);
}

export function computeClosingDrops(drops, creatorById, { niche = "all", search = "", excludeIds = new Set(), limit = null } = {}) {
  const trimmed = search.trim();
  const pool = drops
    .filter((d) => isOpenFeedDrop(d) && !excludeIds.has(d.id))
    .filter((d) => {
      if (niche !== "all" && normalizeNicheKey(d.niche) !== niche) return false;
      if (trimmed) {
        const creator = creatorById.get(d.creatorId);
        const haystack = [d.title, d.description, creator?.displayName, creator?.handle, getNicheMeta(d.niche).label]
          .filter(Boolean)
          .join(" ");
        if (!matchesSearch(haystack, trimmed)) return false;
      }
      return true;
    })
    .sort((a, b) => viewsLeft(a) - viewsLeft(b)); // the ONE ranked pool — ranks a fact, not a score
  return limit == null ? pool : pool.slice(0, limit);
}

export function computeRisingCreators(creators, dropsByCreator, { niche = "all", search = "", seed = 0, limit = RISING_LIMIT } = {}) {
  const trimmed = search.trim();
  const pool = creators
    .map((c) => {
      const creatorDrops = dropsByCreator.get(c.id) || [];
      const openCount = creatorDrops.filter(isOpenFeedDrop).length;
      return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
    })
    .filter((c) => isNewCreator(c, c.totalDrops))
    .filter((c) => {
      if (niche !== "all" && normalizeNicheKey(c.niche) !== niche) return false;
      if (trimmed) {
        const haystack = [c.displayName, c.handle, c.bio, getNicheMeta(c.niche).label].filter(Boolean).join(" ");
        if (!matchesSearch(haystack, trimmed)) return false;
      }
      return true;
    });
  const shuffled = seededShuffle(pool, seed);
  return limit == null ? shuffled : shuffled.slice(0, limit);
}

export function computeCreatorsToFollow(creators, dropsByCreator, followingIds, networkIds, { niche = "all", search = "", excludeIds = new Set(), seed = 0, limit = null } = {}) {
  const trimmed = search.trim();
  const pool = creators
    .filter((c) => !followingIds.includes(c.id))
    .filter((c) => !excludeIds.has(c.id))
    .map((c) => {
      const creatorDrops = dropsByCreator.get(c.id) || [];
      const openCount = creatorDrops.filter(isOpenFeedDrop).length;
      return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
    })
    .filter((c) => {
      if (niche !== "all" && normalizeNicheKey(c.niche) !== niche) return false;
      if (trimmed) {
        const haystack = [c.displayName, c.handle, c.bio, getNicheMeta(c.niche).label].filter(Boolean).join(" ");
        if (!matchesSearch(haystack, trimmed)) return false;
      }
      return c.openCount > 0 || c.totalDrops > 0;
    });

  const shuffled = seededShuffle(pool, seed);
  const sorted = shuffled.sort((a, b) => {
    const aNet = networkIds.has(a.id) ? 1 : 0;
    const bNet = networkIds.has(b.id) ? 1 : 0;
    if (bNet !== aNet) return bNet - aNet;
    return b.openCount - a.openCount; // no followerCount tiebreak
  });
  return limit == null ? sorted : sorted.slice(0, limit);
}

export function computeNearYouCreators(creators, dropsByCreator, followingIds, currentUserCity, { seed = 0, limit = NEAR_YOU_LIMIT } = {}) {
  if (!currentUserCity) return [];
  const pool = creators
    .filter((c) => c.city && c.city.toLowerCase() === currentUserCity.toLowerCase())
    .filter((c) => !followingIds.includes(c.id))
    .map((c) => {
      const creatorDrops = dropsByCreator.get(c.id) || [];
      const openCount = creatorDrops.filter(isOpenFeedDrop).length;
      return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
    })
    .filter((c) => c.openCount > 0 || c.totalDrops > 0);
  const shuffled = seededShuffle(pool, seed);
  return limit == null ? shuffled : shuffled.slice(0, limit);
}